import { useState, useMemo, useEffect } from "react";
import { calculatePagination, PaginationMeta } from "../lib/pagination";

export interface UsePaginationOptions {
  pageSize?: number;
  initialPage?: number;
}

export interface UsePaginationReturn<T> extends PaginationMeta {
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  paginatedItems: T[];
  nextPage: () => void;
  prevPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
}

export function usePagination<T>(
  items: T[],
  pageSizeOrOptions?: number | UsePaginationOptions
): UsePaginationReturn<T> {
  const options: UsePaginationOptions =
    typeof pageSizeOrOptions === "number"
      ? { pageSize: pageSizeOrOptions }
      : pageSizeOrOptions ?? {};

  const pageSize = options.pageSize && options.pageSize > 0 ? options.pageSize : 15;
  const initialPage = options.initialPage && options.initialPage > 0 ? options.initialPage : 1;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  const meta = calculatePagination(items.length, currentPage, pageSize);

  // Auto-clamp caso a lista mude de tamanho
  useEffect(() => {
    if (currentPage > meta.totalPages) {
      setCurrentPage(meta.totalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, meta.totalPages]);

  const paginatedItems = useMemo(() => {
    return items.slice(meta.startIndex, meta.endIndex);
  }, [items, meta.startIndex, meta.endIndex]);

  const nextPage = () => {
    if (meta.hasNextPage) setCurrentPage((p) => Math.min(p + 1, meta.totalPages));
  };

  const prevPage = () => {
    if (meta.hasPrevPage) setCurrentPage((p) => Math.max(p - 1, 1));
  };

  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(meta.totalPages);

  return {
    ...meta,
    setCurrentPage,
    paginatedItems,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
  };
}
